import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { InventoryItem } from '../../../types/inventory';
import { UnitToggle } from '../../../components/common/UnitToggle';
import { WeightInput } from '../../../components/common/WeightInput';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { LotSection } from '../../roasts/component/LotSection';
import type { LotRow } from '../hooks/useWorkOrderForm';
import { OUTPUT_EXCEEDS_INPUT_MESSAGE } from '../hooks/useWorkOrderForm';
import { AutocompleteSelect } from '../../../components/common/AutocompleteSelect';
import { useLanguage } from '../../../features/language/useLanguage';

export const BeanSelectionSection = ({
  isEditMode,
  greenBeans,
  selectedBeanId,
  handleBeanChange,
  handleAutoCalculateLots,
  isAutoCalculatingLots,
  manualInputWeight,
  manualOutputWeight,
  handleWeightInputChange,
  handleWeightOutputChange,
  unit,
  setUnit,
  isCompleted,
  lotOptions,
  lots,
  handleLotChange,
  handleWeightChange,
  addLotRow,
  removeLotRow,
  totalUsedWeight,
  parsedRoastInputWeight,
  isWeightMismatch,
  useRoastProfile,
  roastType,
  setRoastType,
  isOutputExceedsInput,
  allowRoastTypeEdit = false,
}: {
  isEditMode: boolean;
  greenBeans: InventoryItem[];
  selectedBeanId: string;
  handleBeanChange: (value: string) => void;
  handleAutoCalculateLots: () => void;
  isAutoCalculatingLots: boolean;
  manualInputWeight: string;
  manualOutputWeight: string;
  handleWeightInputChange: (val: number | undefined) => void;
  handleWeightOutputChange: (val: number | undefined) => void;
  unit: WeightUnit;
  setUnit: (value: WeightUnit) => void;
  isCompleted: boolean;
  lotOptions: { id: string; name: string; remaining: number; density?: number | null; moisture?: number | null; processType?: string | null }[];
  lots: LotRow[];
  handleLotChange: (index: number, lotId: string) => void;
  handleWeightChange: (index: number, value: number | undefined) => void;
  addLotRow: () => void;
  removeLotRow: (index: number) => void;
  totalUsedWeight: number;
  parsedRoastInputWeight: number;
  isWeightMismatch: boolean;
  useRoastProfile: boolean;
  roastType: string;
  setRoastType: (value: string) => void;
  isOutputExceedsInput: boolean;
  allowRoastTypeEdit?: boolean;
}) => {
  const { t } = useLanguage();
  const beanOptions = useMemo(() => {
    return greenBeans.map(bean => ({
      id: bean.id,
      label: `${bean.name} (${bean.code})`,
      text: `${bean.name} (${bean.code})`,
      value: bean.id,
    }));
  }, [greenBeans]);

  if (!isEditMode) return null;

  return (
    <div className="border-b pb-6">
      <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{t('workOrders.beanSelection.title')}</h3>
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">{t('common.terms.greenBean')}</label>
            <AutocompleteSelect
              dataTestId="wo-green-bean-select"
              value={selectedBeanId}
              onChange={(value) => handleBeanChange(value)}
              options={beanOptions}
              placeholder={t('workOrders.beanSelection.selectGreenBean')}
              disabled={isCompleted}
              className="mt-1"
              allowCustom={false}
              inputClassName="p-2"
              wrapperClassName="rounded-md"
            />
          </div>

          <div className="flex md:w-auto">
            <button
              type="button"
              onClick={handleAutoCalculateLots}
              disabled={
                isAutoCalculatingLots || !selectedBeanId || !manualInputWeight || Number(manualInputWeight) <= 0 || isCompleted
              }
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-test-id="wo-auto-calc-lots-button"
            >
              {isAutoCalculatingLots ? t('workOrders.beanSelection.calculating') : t('workOrders.beanSelection.autoCalculate')}
            </button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">{t('workOrders.table.roastInput')}</label>
              <UnitToggle unit={unit} onToggle={setUnit} />
            </div>
            <WeightInput
              value={manualInputWeight === '' ? undefined : Number(manualInputWeight)}
              onChange={val => handleWeightInputChange(val)}
              unit={unit}
              disabled={isCompleted}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="wo-input-weight"
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span>{t('workOrders.table.roastOutput')}</span>
                <span className="text-xs text-gray-500">{t('workOrders.beanSelection.optional')}</span>
                {isOutputExceedsInput && <span className="text-xs text-red-600">{t(OUTPUT_EXCEEDS_INPUT_MESSAGE)}</span>}
              </label>
              <UnitToggle unit={unit} onToggle={setUnit} />
            </div>
            <WeightInput
              value={manualOutputWeight === '' ? undefined : Number(manualOutputWeight)}
              onChange={val => handleWeightOutputChange(val)}
              unit={unit}
              disabled={isCompleted}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="wo-output-weight"
            />
          </div>
        </div>

        {selectedBeanId && lotOptions.length === 0 && !isCompleted && (
          <div
            role="status"
            className="mt-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4"
            data-test-id="wo-no-lots-warning"
          >
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900">
                {t('workOrders.beanSelection.noLotsTitle')}
              </p>
              <p className="text-xs text-amber-800">
                {t('workOrders.beanSelection.noLotsHint')}
              </p>
            </div>
          </div>
        )}

        {selectedBeanId && lotOptions.length > 0 && (
          <div className="mt-4 space-y-4">
            <LotSection
              lots={lots}
              lotOptions={lotOptions}
              onLotChange={handleLotChange}
              onWeightChange={handleWeightChange}
              onAdd={addLotRow}
              onRemove={removeLotRow}
              totalUsedWeight={totalUsedWeight}
              roastInputWeight={parsedRoastInputWeight}
              showMismatchWarning={isWeightMismatch}
              hideStockInfo={isCompleted}
              disableUseWeight={isCompleted}
              unit={unit}
              onToggle={setUnit}
            />
          </div>
        )}

        {!useRoastProfile && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">{t('workOrders.table.roastType')} *</label>
            <select
              value={roastType}
              onChange={e => setRoastType(e.target.value)}
              disabled={isCompleted && !allowRoastTypeEdit}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100"
              data-test-id="wo-roast-type-input"
            >
              <option value="">{t('workOrders.beanSelection.selectRoastType')}</option>
              <option value="sample">{t('workOrders.beanSelection.roast.sample')}</option>
              <option value="filter">{t('workOrders.beanSelection.roast.filter')}</option>
              <option value="omni">{t('workOrders.beanSelection.roast.omni')}</option>
              <option value="espresso">{t('workOrders.beanSelection.roast.espresso')}</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};
